import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { theme } from '../theme';
import ReportsModal from '../components/ReportsModal';
import Navbar from '../components/Navbar';
import { styles, sidebarStyles } from './admin/components/adminStyles';
import AdminOverview from './admin/components/AdminOverview';
import ActionLibrary from './admin/components/ActionLibrary';
import AlgorithmLibrary from './admin/components/AlgorithmLibrary';
import LayerLibrary from './admin/components/LayerLibrary';
import AuctionManagement, { ClosureModal } from './admin/components/AuctionManagement';
import UserManagement from './admin/components/UserManagement';
import AdminReports from './admin/components/AdminReports';
import EventArchive from './admin/components/EventArchive';
import ContractsDocuments from './admin/components/ContractsDocuments';
import {
  HomeIcon, UsersIcon, SlidersIcon, BookOpenIcon, ChartBarIcon,
  ShieldIcon, LockIcon, DocumentIcon, SigmaIcon, LayersIcon, ArchiveIcon
} from './admin/components/navIcons';

// Persists which sidebar tab is active across ClientSelector's full-page
// reload (switching client/tenant reloads the whole app), so a superadmin
// switching clients isn't dropped back to Overview every time.
const ACTIVE_TAB_STORAGE_KEY = 'adminActiveTab';

// Manual events only — seeded default for allowedActions[].questions,
// matching the backend Event model's default (models/Event.js) and the
// farmer-facing labels these replace (AuctionBiddingView.js's ProposalModal).
const DEFAULT_MANUAL_QUESTIONS = ['Proposal Overview', 'Expected Environmental Impact', 'Evaluation & Verification Plan'];

const PRIMARY_NAV_ITEMS = [
  { key: 'overview', label: 'Live Events', icon: <HomeIcon /> },
  { key: 'algorithms', label: 'Outcome Algorithms', icon: <SigmaIcon /> },
  { key: 'library', label: 'Action Library', icon: <BookOpenIcon /> },
  { key: 'dashboard', label: 'Create Event', icon: <SlidersIcon /> },
  { key: 'layerLibrary', label: 'Layer Library', icon: <LayersIcon /> },
  { key: 'users', label: 'Users', icon: <UsersIcon /> },
  { key: 'reports', label: 'Reports', icon: <ChartBarIcon /> },
  { key: 'archive', label: 'Event Archive', icon: <ArchiveIcon /> },
  { key: 'contracts', label: 'Contracts & Documents', icon: <DocumentIcon /> },
];

const COMING_SOON_ITEMS = [];

const SidebarLink = ({ icon, label, hint, active, collapsed, disabled, tone, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const activeStyle = tone === 'danger' ? sidebarStyles.superLinkActive : sidebarStyles.linkActive;
  const hoverStyle = tone === 'danger' ? sidebarStyles.superLinkHover : sidebarStyles.linkHover;

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={collapsed ? label : undefined}
      style={{
        ...sidebarStyles.link,
        ...(tone === 'danger' ? sidebarStyles.superLink : {}),
        ...(active ? activeStyle : (hovered && !disabled ? hoverStyle : {})),
        ...(disabled ? sidebarStyles.linkDisabled : {}),
        justifyContent: collapsed ? 'center' : 'flex-start'
      }}
    >
      <span style={sidebarStyles.linkIcon}>{icon}</span>
      {!collapsed && (
        <span style={sidebarStyles.linkTextCol}>
          <span style={sidebarStyles.linkLabel}>{label}</span>
          {hint && <span style={sidebarStyles.linkHint}>{hint}</span>}
        </span>
      )}
      {!collapsed && disabled && <span style={sidebarStyles.lockIcon}><LockIcon /></span>}
    </button>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // --- GLOBAL DATA ---
  const [events, setEvents] = useState([]);
  const [libraryActions, setLibraryActions] = useState([]);
  const [archivedActions, setArchivedActions] = useState([]);
  const [zones, setZones] = useState([]);
  const [globalZones, setGlobalZones] = useState([]);

  // --- LAYER LIBRARY (reusable, tenant-scoped GeoJSON layers) ---
  const [eligibilityLayers, setEligibilityLayers] = useState([]);
  const [focusAreaLayers, setFocusAreaLayers] = useState([]);
  const [layerLibrary, setLayerLibrary] = useState([]);
  const [layerLibraryLoading, setLayerLibraryLoading] = useState(false);

  // --- LIVE BIDS ---
  const [liveBids, setLiveBids] = useState([]);

  // --- BID DETAILS MODAL (triggered from Overview's "View Bids" action) ---
  const [modalLiveBids, setModalLiveBids] = useState([]);
  const [modalZones, setModalZones] = useState([]);
  const [modalBidsLoading, setModalBidsLoading] = useState(false);

  // --- OVERVIEW DATA ---
  const [overviewData, setOverviewData] = useState({});
  const [overviewLoading, setOverviewLoading] = useState(false);
  // Per-auction { hasFocusArea } flag for the Summary of Auctions table.
  const [zoneCoverage, setZoneCoverage] = useState({});

  // Sorting & Filtering State
  const [sortConfig, setSortConfig] = useState({ key: 'status', direction: 'desc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getProcessedEvents = useCallback(() => {
    let processed = [...events];
    processed.sort((a, b) => {
      const { key, direction } = sortConfig;
      let valA = a[key];
      let valB = b[key];
      if (key === 'endDate') { valA = new Date(valA); valB = new Date(valB); } 
      else if (typeof valA === 'string') { valA = valA.toLowerCase(); valB = valB.toLowerCase(); }
      
      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      if (key !== 'endDate') {
         const dateA = new Date(a.endDate);
         const dateB = new Date(b.endDate);
         return dateB - dateA; 
      }
      return 0;
    });
    return processed;
  }, [events, sortConfig]);

  const processedEvents = getProcessedEvents();

  const [editName, setEditName] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  // 👇 NEW: Edit State for new Event fields
  const [editMaxPayout, setEditMaxPayout] = useState('');
  const [editTerms, setEditTerms] = useState('');
  const [editEventSummary, setEditEventSummary] = useState('');
  const [editEventSummaryUrl, setEditEventSummaryUrl] = useState('');
  // competitive_fundspreader only — see AuctionManagement.js's Clearing Metric picker.
  const [editFundspreaderClearingMetric, setEditFundspreaderClearingMetric] = useState('area');
  const [editFundspreaderOutcomeToken, setEditFundspreaderOutcomeToken] = useState('');

  const [hasChanges, setHasChanges] = useState(false);

  // --- UI STATE ---
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) || 'overview');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [zonesModalAuctionId, setZonesModalAuctionId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [simLoading, setSimLoading] = useState(false);

  // Action Modal State
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [wholeFarmModalMode, setWholeFarmModalMode] = useState(false);
  const [isAlgorithmModalOpen, setIsAlgorithmModalOpen] = useState(false);
  const [editingAlgorithm, setEditingAlgorithm] = useState(null);

  // --- FORMS ---
  // 👇 UPDATED: Added new fields to Create Form state
  const [newEvent, setNewEvent] = useState({
      name: '', startDate: '', endDate: '', auctionType: 'grants_fixed',
      selectedActionIds: {}, outcomeValues: {}, outcomeValuesTouched: {}, minBenefitCostRatio: 1,
      maxPayoutPerFarmer: '', termsAndConditions: '',
      eventSummary: '', eventSummaryUrl: '',
      fundspreaderClearingMetric: 'area', fundspreaderOutcomeToken: '',
      isGlobal: false, clientId: ''
  });

  // Only loaded/used for superadmins creating a Client-Specific auction on
  // another tenant's behalf (see the client-picker in AuctionManagement).
  const [clients, setClients] = useState([]);

  // Outcome Algorithms library, for binding to Action outcomes in ActionModal.
  const [algorithms, setAlgorithms] = useState([]);

  const [editOutcomeValues, setEditOutcomeValues] = useState({});
  const [editOutcomeValuesTouched, setEditOutcomeValuesTouched] = useState({});
  const [editMinBenefitCostRatio, setEditMinBenefitCostRatio] = useState(2.0);
  const [editActions, setEditActions] = useState([]);
  const [auctionResults, setAuctionResults] = useState(null);
  
  // Report State — one config drives the single registry-backed ReportsModal
  // (replaces the old reportUserId/globalReportType/reportEventInfo trio,
  // each of which opened its own hardcoded modal component).
  const [reportsModalConfig, setReportsModalConfig] = useState(null); // null | { reportKey?, params }
  const [showClosureModal, setShowClosureModal] = useState(false);

  // --- LOADERS ---
  const loadEvents = useCallback(async () => {
    try {
      const res = await api.get('/events?t=' + Date.now());
      setEvents(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const loadLibrary = useCallback(async () => {
      try { const res = await api.get('/actions'); setLibraryActions(res.data); } catch (e) {}
  }, []);

  const loadArchivedActions = useCallback(async () => {
      try {
          const res = await api.get('/actions?includeArchived=true');
          setArchivedActions(res.data.filter(a => a.isArchived));
      } catch (e) {}
  }, []);

  const loadAlgorithms = useCallback(async () => {
      try { const res = await api.get('/algorithms?includeArchived=true'); setAlgorithms(res.data); } catch (e) {}
  }, []);


  const loadZones = useCallback(async (id) => {
      try { const res = await api.get(`/zones/${id}`); setZones(res.data); } catch (e) {} 
  }, []);

  const loadGlobalZones = useCallback(async () => {
      try { const res = await api.get('/zones/global'); setGlobalZones(res.data); } catch(e){}
  }, []);

  const loadLayers = useCallback(async (layerType, setter) => {
      try { const res = await api.get(`/layers?layerType=${layerType}`); setter(res.data); } catch (e) {}
  }, []);

  const loadEligibilityLayers = useCallback(() => loadLayers('eligibility', setEligibilityLayers), [loadLayers]);
  const loadFocusAreaLayers = useCallback(() => loadLayers('priority', setFocusAreaLayers), [loadLayers]);

  const loadLayerLibrary = useCallback(async () => {
      setLayerLibraryLoading(true);
      try { const res = await api.get('/layers?includeArchived=true'); setLayerLibrary(res.data); }
      catch (e) {}
      finally { setLayerLibraryLoading(false); }
  }, []);


  const loadLiveBids = useCallback(async (id) => {
      if (!id) return;
      try {
          const res = await api.get(`/admin/bids/${id}`);
          setLiveBids(res.data);
      }
      catch (err) { }
  }, []);

  const loadBidsForAuction = useCallback(async (auctionId) => {
      if (!auctionId) return;
      setModalBidsLoading(true);
      try {
          const [bidsRes, zonesRes] = await Promise.all([
              api.get(`/admin/bids/${auctionId}`),
              api.get(`/zones/${auctionId}`)
          ]);
          setModalLiveBids(bidsRes.data);
          setModalZones(zonesRes.data);
      } catch (err) {
          console.error('Failed to load bids for modal', err);
      } finally {
          setModalBidsLoading(false);
      }
  }, []);

  const handleSaveDecision = async (bidId, decisionStatus, adminDecisionComments, auctionId) => {
      try {
          await api.put(`/admin/bids/${bidId}/decision`, { decisionStatus, adminDecisionComments });
          if (auctionId) loadBidsForAuction(auctionId);
      } catch (err) {
          alert(err.response?.data?.msg || 'Failed to save decision.');
      }
  };

  // Ports SuperAdmin's Bid Force-Delete down to regular Admins (tenant-scoped
  // server-side via assertEventOwnership) — reason is required and gets
  // emailed to the farmer plus every admin/superadmin.
  const handleDeleteBid = async (bidId, reason, auctionId) => {
      try {
          await api.delete(`/admin/bids/${bidId}`, { data: { reason } });
          if (auctionId) loadBidsForAuction(auctionId);
      } catch (err) {
          alert(err.response?.data?.msg || 'Failed to delete bid.');
      }
  };

  // Manual Bid Override & Winner Promotion (post-closure contract edge cases).
  // Both refresh the modal's own bid list AND the Overview table's spend
  // totals (loadOverviewStats) — the override changes payoutAmount, which
  // GET /events/overview-stats sums directly, so the Overview table would
  // otherwise show stale figures until the admin manually reloaded the tab.
  const handleWithdrawWinner = async (bidId, reason, auctionId) => {
      try {
          await api.put(`/admin/bids/${bidId}/withdraw-winner`, { reason });
          if (auctionId) loadBidsForAuction(auctionId);
          loadOverviewStats();
      } catch (err) {
          alert(err.response?.data?.msg || 'Failed to withdraw winning bid.');
      }
  };

  const handlePromoteBid = async (bidId, reason, payoutAmount, auctionId) => {
      try {
          await api.put(`/admin/bids/${bidId}/promote`, { reason, payoutAmount });
          if (auctionId) loadBidsForAuction(auctionId);
          loadOverviewStats();
      } catch (err) {
          const data = err.response?.data;
          if (data?.maxPromotableAmount !== undefined) {
              alert(`${data.msg} Maximum promotable amount right now: £${Number(data.maxPromotableAmount).toLocaleString('en-GB')}.`);
          } else {
              alert(data?.msg || 'Failed to promote bid.');
          }
          throw err;
      }
  };

  const fetchPromotionHeadroom = async (bidId) => {
      const res = await api.get(`/admin/bids/${bidId}/promotion-headroom`);
      return res.data;
  };

  // Automated version of the pair above: re-ranks every standby bid on the
  // event against current headroom instead of promoting one bid at a time.
  const fetchBackfillPreview = async (auctionId) => {
      const res = await api.get(`/admin/events/${auctionId}/backfill-preview`);
      return res.data;
  };

  const handleBackfillWinners = async (auctionId) => {
      const res = await api.post(`/admin/events/${auctionId}/backfill-winners`);
      if (auctionId) loadBidsForAuction(auctionId);
      loadOverviewStats();
      return res.data;
  };

  const loadOverviewStats = useCallback(async () => {
      if (events.length === 0) return;
      setOverviewLoading(true);
      try {
          // Single aggregation call (GET /events/overview-stats) instead of the
          // old one-request-per-event fan-out into the live-scoring
          // /admin/bids/:eventId endpoint — that N+1 pattern was the ~5s
          // Overview load-time culprit (see routes/events.js for details).
          const res = await api.get('/events/overview-stats');
          setOverviewData(res.data || {});
      } catch (err) { console.error(err); }
      finally { setOverviewLoading(false); }
  }, [events]);

  const loadZoneCoverage = useCallback(async () => {
      try { const res = await api.get('/zones/summary'); setZoneCoverage(res.data); } catch (e) {}
  }, []);

  // Only superadmins can create Global auctions or pick a client on another
  // tenant's behalf, so only they need the full client list.
  const loadClients = useCallback(async () => {
      try { const res = await api.get('/clients'); setClients(res.data); } catch (e) {}
  }, []);

  useEffect(() => { loadEvents(); loadLibrary(); loadAlgorithms(); loadGlobalZones(); loadEligibilityLayers(); }, [loadEvents, loadLibrary, loadAlgorithms, loadGlobalZones, loadEligibilityLayers]);

  useEffect(() => {
      if (user?.role === 'superadmin') loadClients();
  }, [user, loadClients]);

  useEffect(() => {
      if (activeTab === 'overview' && events.length > 0) {
          loadOverviewStats();
          loadZoneCoverage();
      }
  }, [activeTab, events, loadOverviewStats, loadZoneCoverage]);

  useEffect(() => {
      if (activeTab === 'layerLibrary') loadLayerLibrary();
  }, [activeTab, loadLayerLibrary]);

  useEffect(() => {
      localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
      setAuctionResults(null);
  }, [selectedEvent]);

  const onLogout = () => { logout(); navigate('/login'); };

  // --- HANDLERS ---
  const handleSaveAction = async (actionPayload) => {
      // A superadmin with no client impersonated has req.clientId=null server-side,
      // so a brand-new action would silently land in the shared/global pool
      // (visible to every tenant) instead of a specific tenant's library.
      if (!editingAction && user?.role === 'superadmin' && !localStorage.getItem('impersonatedClientId')) {
          const proceed = window.confirm(
              'No client is currently impersonated (tenant switcher is set to "— My Default —"). ' +
              'This action will be created as a SHARED action visible in every tenant\'s Action Library.\n\n' +
              'Select a client from the tenant switcher first if this action should be private to one tenant.\n\n' +
              'Create it as a shared/global action anyway?'
          );
          if (!proceed) return;
      }
      try {
          if (editingAction) {
              await api.put(`/actions/${editingAction._id}`, actionPayload);
              alert("Action updated successfully!");
          } else {
              await api.post('/actions', actionPayload);
              alert("Action created successfully!");
          }
          setIsActionModalOpen(false);
          setEditingAction(null);
          setWholeFarmModalMode(false);
          loadLibrary();
      } catch (err) {
          alert("Failed to save action: " + (err.response?.data?.msg || err.message));
      }
  };

  const handleDeleteAction = async (actionId, actionName) => {
      if (!window.confirm(`Are you sure you want to delete "${actionName}"?`)) return;
      try {
          await api.delete(`/actions/${actionId}`);
          loadLibrary();
      } catch (err) {
          alert(err.response?.data?.msg || "Failed to delete action");
      }
  };

  const handleArchiveAction = async (actionId) => {
      try {
          await api.put(`/actions/${actionId}/archive`, { isArchived: true });
          await Promise.all([loadLibrary(), loadArchivedActions()]);
      } catch (err) {
          alert(err.response?.data?.msg || "Failed to archive action");
      }
  };

  const handleRestoreAction = async (actionId) => {
      try {
          await api.put(`/actions/${actionId}/archive`, { isArchived: false });
          await Promise.all([loadLibrary(), loadArchivedActions()]);
      } catch (err) {
          alert(err.response?.data?.msg || "Failed to restore action");
      }
  };

  const handleSaveAlgorithm = async (algorithmPayload) => {
      try {
          if (editingAlgorithm) {
              await api.put(`/algorithms/${editingAlgorithm._id}`, algorithmPayload);
              alert("Algorithm updated successfully!");
          } else {
              await api.post('/algorithms', algorithmPayload);
              alert("Algorithm created successfully!");
          }
          setIsAlgorithmModalOpen(false);
          setEditingAlgorithm(null);
          loadAlgorithms();
      } catch (err) {
          alert("Failed to save algorithm: " + (err.response?.data?.msg || err.message));
      }
  };

  const handleArchiveToggleAlgorithm = async (algorithmId, archive) => {
      try {
          await api.put(`/algorithms/${algorithmId}/archive`, { status: archive ? 'archived' : 'active' });
          loadAlgorithms();
      } catch (err) {
          alert(err.response?.data?.msg || `Failed to ${archive ? 'archive' : 'restore'} algorithm`);
      }
  };

  const handleDeleteAlgorithm = async (algorithmId) => {
      try {
          await api.delete(`/algorithms/${algorithmId}`);
          loadAlgorithms();
      } catch (err) {
          alert(err.response?.data?.msg || "Failed to delete algorithm");
      }
  };

  const handleArchiveToggleLayer = async (layerId, archive) => {
      try {
          await api.put(`/layers/${layerId}/archive`, { status: archive ? 'archived' : 'active' });
          loadLayerLibrary();
      } catch (err) {
          alert(err.response?.data?.msg || `Failed to ${archive ? 'archive' : 'restore'} layer`);
      }
  };

  const handleDeleteLayer = async (layerId) => {
      try {
          await api.delete(`/layers/${layerId}`);
          loadLayerLibrary();
      } catch (err) {
          alert(err.response?.data?.msg || "Failed to delete layer");
      }
  };

  // Throws on failure — AlgorithmLibrary's CsvUploadSection owns its own
  // busy/status state and surfaces err.response?.data?.errors itself.
  const handleUploadAlgorithmsCsv = async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/algorithms/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadAlgorithms();
      return res.data;
  };

  const openWholeFarmModal = () => {
      setEditingAction(null);
      setWholeFarmModalMode(true);
      setIsActionModalOpen(true);
  };

  // Every distinct outcome NAME (not algorithm) delivered across the given
  // actions' outcomes[] — £ valuation is now set once per outcome name at
  // the Event level (see AuctionManagement.js's "Outcome Values" grid and
  // formulaEvaluator.js), not per algorithm/action, so there's no
  // per-action value to seed from anymore — every outcome just defaults to 0
  // until the admin sets it.
  const computeOutcomeDefaults = useCallback((actions) => {
      const defaults = {};
      (actions || []).forEach(act => {
          (act.outcomes || []).forEach(o => {
              const alg = algorithms.find(a => a._id === o.outcomeAlgorithmId);
              if (!alg?.outcomeType) return;
              if (defaults[alg.outcomeType] === undefined) defaults[alg.outcomeType] = 0;
          });
      });
      return defaults;
  }, [algorithms]);

  // Builds the outcomeValues map for the currently-selected actions. An
  // outcome the admin has manually edited (tracked in `touched`) keeps its
  // value regardless of what the selected actions currently offer; an
  // untouched outcome always tracks the live highest baseValuePerUnit, so
  // adding a higher-value action bumps it up and removing that action drops
  // it back down. Outcomes no longer offered by any selected action are
  // dropped from the result entirely.
  const seedOutcomeValues = (defaults, existingValues, touched) => {
      const result = {};
      Object.entries(defaults || {}).forEach(([id, defaultVal]) => {
          result[id] = (touched?.[id] && existingValues?.[id] !== undefined) ? existingValues[id] : defaultVal;
      });
      return result;
  };

  // Trims a `touched` map down to only the outcome ids still present, so
  // stale flags for outcomes that dropped out of the selection don't linger.
  const pruneTouched = (touched, defaults) => {
      const result = {};
      Object.keys(defaults || {}).forEach(id => { if (touched?.[id]) result[id] = true; });
      return result;
  };

  const isWholeFarmActionType = (actionType) => !!actionType && actionType !== 'field_based';

  const toggleNewEventAction = (actionId) => {
      const current = newEvent.selectedActionIds[actionId] || { enabled: false, budget: 100000, grantPrice: 0, maxPaymentPerFarm: null, maxContributionPercent: 100, maxPaymentPerUnit: null };
      const updatedSelection = { ...newEvent.selectedActionIds, [actionId]: { ...current, enabled: !current.enabled } };

      const activeCreateActions = libraryActions.filter(act => updatedSelection[act._id]?.enabled);
      const defaults = computeOutcomeDefaults(activeCreateActions);
      const newValues = seedOutcomeValues(defaults, newEvent.outcomeValues, newEvent.outcomeValuesTouched);
      const newTouched = pruneTouched(newEvent.outcomeValuesTouched, defaults);
      setNewEvent({ ...newEvent, selectedActionIds: updatedSelection, outcomeValues: newValues, outcomeValuesTouched: newTouched });
  };

  const handleAuctionTypeChange = (auctionType) => {
      // Type 2 (whole_farm_manual) is reserved for 'manual' auctions only;
      // every other auction type may offer Type 1 (field_based) and Type 3
      // (whole_farm_non_manual) actions.
      const filteredSelection = {};
      Object.entries(newEvent.selectedActionIds).forEach(([actionId, sel]) => {
          const act = libraryActions.find(a => a._id === actionId);
          if (!act) return;
          const allowed = auctionType === 'manual' ? act.actionType === 'whole_farm_manual' : act.actionType !== 'whole_farm_manual';
          if (allowed) filteredSelection[actionId] = sel;
      });

      const activeCreateActions = libraryActions.filter(act => filteredSelection[act._id]?.enabled);
      const defaults = computeOutcomeDefaults(activeCreateActions);
      const newValues = seedOutcomeValues(defaults, newEvent.outcomeValues, newEvent.outcomeValuesTouched);
      const newTouched = pruneTouched(newEvent.outcomeValuesTouched, defaults);

      setNewEvent({ ...newEvent, auctionType, selectedActionIds: filteredSelection, outcomeValues: newValues, outcomeValuesTouched: newTouched });
  };

  const updateNewEventBudget = (actionId, val) => {
      const current = newEvent.selectedActionIds[actionId] || { enabled: true, budget: 100000, grantPrice: 0, maxPaymentPerFarm: null, maxContributionPercent: 100, maxPaymentPerUnit: null };
      setNewEvent({ ...newEvent, selectedActionIds: { ...newEvent.selectedActionIds, [actionId]: { ...current, budget: parseInt(val) || 0 } } });
  };

  // 👇 NEW: Handle grant price updates in create form
  const updateNewEventGrantPrice = (actionId, val) => {
      const current = newEvent.selectedActionIds[actionId] || { enabled: true, budget: 100000, grantPrice: 0, maxPaymentPerFarm: null, maxContributionPercent: 100, maxPaymentPerUnit: null };
      setNewEvent({ ...newEvent, selectedActionIds: { ...newEvent.selectedActionIds, [actionId]: { ...current, grantPrice: parseFloat(val) || 0 } } });
  };

  const updateNewEventMaxPaymentPerFarm = (actionId, val) => {
      const current = newEvent.selectedActionIds[actionId] || { enabled: true, budget: 100000, grantPrice: 0, maxPaymentPerFarm: null, maxContributionPercent: 100, maxPaymentPerUnit: null };
      setNewEvent({ ...newEvent, selectedActionIds: { ...newEvent.selectedActionIds, [actionId]: { ...current, maxPaymentPerFarm: val === '' ? null : parseFloat(val) || 0 } } });
  };

  const updateNewEventMaxContributionPercent = (actionId, val) => {
      const current = newEvent.selectedActionIds[actionId] || { enabled: true, budget: 100000, grantPrice: 0, maxPaymentPerFarm: null, maxContributionPercent: 100, maxPaymentPerUnit: null };
      setNewEvent({ ...newEvent, selectedActionIds: { ...newEvent.selectedActionIds, [actionId]: { ...current, maxContributionPercent: val === '' ? 100 : parseFloat(val) || 0 } } });
  };

  const updateNewEventMaxPaymentPerUnit = (actionId, val) => {
      const current = newEvent.selectedActionIds[actionId] || { enabled: true, budget: 100000, grantPrice: 0, maxPaymentPerFarm: null, maxContributionPercent: 100, maxPaymentPerUnit: null };
      setNewEvent({ ...newEvent, selectedActionIds: { ...newEvent.selectedActionIds, [actionId]: { ...current, maxPaymentPerUnit: val === '' ? null : parseFloat(val) || 0 } } });
  };

  // 👇 NEW: Extra £/unit grant payment for a specific Action Variation option
  // (fixed-rate grants only). optionKey is "variationName|optionName".
  const updateNewEventOptionGrantPayment = (actionId, optionKey, val) => {
      const current = newEvent.selectedActionIds[actionId] || { enabled: true, budget: 100000, grantPrice: 0, maxPaymentPerFarm: null, maxContributionPercent: 100, maxPaymentPerUnit: null, optionGrantPayments: {} };
      const updatedPayments = { ...(current.optionGrantPayments || {}), [optionKey]: val === '' ? 0 : (parseFloat(val) || 0) };
      setNewEvent({ ...newEvent, selectedActionIds: { ...newEvent.selectedActionIds, [actionId]: { ...current, optionGrantPayments: updatedPayments } } });
  };

  // Manual events only: one of the 3 admin-editable proposal-form question
  // prompts for this action (see AuctionBiddingView.js's ProposalModal).
  const updateNewEventQuestion = (actionId, questionIndex, val) => {
      const current = newEvent.selectedActionIds[actionId] || { enabled: true, budget: 100000, grantPrice: 0, maxPaymentPerFarm: null, maxContributionPercent: 100, maxPaymentPerUnit: null };
      const questions = [...(current.questions || DEFAULT_MANUAL_QUESTIONS)];
      questions[questionIndex] = val;
      setNewEvent({ ...newEvent, selectedActionIds: { ...newEvent.selectedActionIds, [actionId]: { ...current, questions } } });
  };

  // Manual events only: whether the farmer's "Value of grant requested" field
  // is required for this action (e.g. an EOI action has no upfront £ ask).
  const updateNewEventFundingOptional = (actionId, val) => {
      const current = newEvent.selectedActionIds[actionId] || { enabled: true, budget: 100000, grantPrice: 0, maxPaymentPerFarm: null, maxContributionPercent: 100, maxPaymentPerUnit: null };
      setNewEvent({ ...newEvent, selectedActionIds: { ...newEvent.selectedActionIds, [actionId]: { ...current, fundingOptional: val } } });
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    const finalActions = libraryActions.filter(act => newEvent.selectedActionIds[act._id]?.enabled).map(act => ({
        actionId: act._id,
        label: act.name,
        budget: newEvent.selectedActionIds[act._id].budget || 0,
        grantPrice: newEvent.selectedActionIds[act._id].grantPrice || 0, // 👈 NEW
        maxPaymentPerFarm: newEvent.selectedActionIds[act._id].maxPaymentPerFarm ?? null,
        maxContributionPercent: newEvent.selectedActionIds[act._id].maxContributionPercent ?? 100,
        maxPaymentPerUnit: newEvent.selectedActionIds[act._id].maxPaymentPerUnit ?? null,
        optionGrantPayments: newEvent.selectedActionIds[act._id].optionGrantPayments || {},
        questions: newEvent.selectedActionIds[act._id].questions || DEFAULT_MANUAL_QUESTIONS,
        fundingOptional: newEvent.selectedActionIds[act._id].fundingOptional || false,
        unit: act.unit,
        isWholeFarm: isWholeFarmActionType(act.actionType)
    }));
    if (finalActions.length === 0) return alert("Select at least one action.");
    const outcomeValueEntries = Object.values(newEvent.outcomeValues || {});
    if (outcomeValueEntries.length > 0 && !outcomeValueEntries.some(v => v > 0)) {
        return alert("At least one delivered outcome must have a value above £0.");
    }
    try {
      const isSuperAdmin = user?.role === 'superadmin';
      await api.post('/events', {
        name: newEvent.name,
        startDate: newEvent.startDate,
        endDate: newEvent.endDate,
        auctionType: newEvent.auctionType,
        maxPayoutPerFarmer: newEvent.maxPayoutPerFarmer ? parseFloat(newEvent.maxPayoutPerFarmer) : null, // 👈 NEW
        minBenefitCostRatio: (newEvent.minBenefitCostRatio === '' || newEvent.minBenefitCostRatio === null || newEvent.minBenefitCostRatio === undefined) ? 1 : newEvent.minBenefitCostRatio,
        termsAndConditions: newEvent.termsAndConditions, // 👈 NEW
        eventSummary: newEvent.eventSummary,
        eventSummaryUrl: newEvent.eventSummaryUrl,
        allowedActions: finalActions,
        outcomeValues: newEvent.outcomeValues,
        fundspreaderClearingMetric: newEvent.fundspreaderClearingMetric || 'area',
        fundspreaderOutcomeToken: newEvent.fundspreaderOutcomeToken || null,
        // Only superadmins can set these; the server re-validates role regardless.
        ...(isSuperAdmin ? {
            isGlobal: newEvent.isGlobal,
            ...(!newEvent.isGlobal && newEvent.clientId ? { clientId: newEvent.clientId } : {})
        } : {})
      });
      alert("Event Configured!"); resetNewEventForm(); loadEvents();
      setActiveTab('overview');
    } catch (err) { alert(err.response?.data?.msg || "Failed to save."); }
  };

  const toggleEditAction = (index) => {
      const updated = [...editActions];
      updated[index].enabled = !updated[index].enabled;
      setEditActions(updated);

      const enabledLibActions = updated.filter(a => a.enabled)
          .map(ea => libraryActions.find(la => la._id === ea._id))
          .filter(Boolean);
      const defaults = computeOutcomeDefaults(enabledLibActions);
      setEditOutcomeValues(prev => seedOutcomeValues(defaults, prev, editOutcomeValuesTouched));
      setEditOutcomeValuesTouched(prev => pruneTouched(prev, defaults));
      setHasChanges(true);
  };

  const updateEditActionBudget = (index, val) => {
      const updated = [...editActions]; updated[index].budget = parseInt(val) || 0; setEditActions(updated);
  };

  // 👇 NEW: Handle grant price updates in edit form
  const updateEditActionGrantPrice = (index, val) => {
      const updated = [...editActions]; updated[index].grantPrice = parseFloat(val) || 0; setEditActions(updated);
  };

  const updateEditActionMaxPaymentPerFarm = (index, val) => {
      const updated = [...editActions]; updated[index].maxPaymentPerFarm = val === '' ? null : parseFloat(val) || 0; setEditActions(updated);
  };

  const updateEditActionMaxContributionPercent = (index, val) => {
      const updated = [...editActions]; updated[index].maxContributionPercent = val === '' ? 100 : parseFloat(val) || 0; setEditActions(updated);
  };

  const updateEditActionMaxPaymentPerUnit = (index, val) => {
      const updated = [...editActions]; updated[index].maxPaymentPerUnit = val === '' ? null : parseFloat(val) || 0; setEditActions(updated);
  };

  // 👇 NEW: Extra £/unit grant payment for a specific Action Variation option
  // (fixed-rate grants only). optionKey is "variationName|optionName".
  const updateEditActionOptionGrantPayment = (index, optionKey, val) => {
      const updated = [...editActions];
      updated[index].optionGrantPayments = { ...(updated[index].optionGrantPayments || {}), [optionKey]: val === '' ? 0 : (parseFloat(val) || 0) };
      setEditActions(updated);
  };

  // Manual events only: one of the 3 admin-editable proposal-form question
  // prompts for this action.
  const updateEditActionQuestion = (index, questionIndex, val) => {
      const updated = [...editActions];
      const questions = [...(updated[index].questions || DEFAULT_MANUAL_QUESTIONS)];
      questions[questionIndex] = val;
      updated[index] = { ...updated[index], questions };
      setEditActions(updated);
      setHasChanges(true);
  };

  // Manual events only: whether the farmer's "Value of grant requested" field
  // is required for this action.
  const updateEditActionFundingOptional = (index, val) => {
      const updated = [...editActions];
      updated[index] = { ...updated[index], fundingOptional: val };
      setEditActions(updated);
      setHasChanges(true);
  };

  const handleCreateWeightChange = (outcomeId, val) => {
      setNewEvent(prev => ({
          ...prev,
          outcomeValues: { ...prev.outcomeValues, [outcomeId]: parseFloat(val) || 0 },
          outcomeValuesTouched: { ...prev.outcomeValuesTouched, [outcomeId]: true }
      }));
  };

  const handleEditWeightChange = (outcomeId, val) => {
      setEditOutcomeValues(prev => ({ ...prev, [outcomeId]: parseFloat(val) || 0 }));
      setEditOutcomeValuesTouched(prev => ({ ...prev, [outcomeId]: true }));
      setHasChanges(true);
  };

  const handleUpdateEventSettings = async (bypassFlag = false) => {
      const confirmDeletion = bypassFlag === true;
      if(!confirmDeletion && !window.confirm("Update event settings?")) return;
      if (!editName || !editName.trim()) return alert("Event Name is required.");

      const editOutcomeValueEntries = Object.values(editOutcomeValues || {});
      if (editOutcomeValueEntries.length > 0 && !editOutcomeValueEntries.some(v => v > 0)) {
          return alert("At least one delivered outcome must have a value above £0.");
      }

      const finalActions = editActions.filter(a => a.enabled).map(a => ({
          actionId: a._id, label: a.label, budget: a.budget, grantPrice: a.grantPrice,
          maxPaymentPerFarm: a.maxPaymentPerFarm ?? null, maxContributionPercent: a.maxContributionPercent ?? 100,
          maxPaymentPerUnit: a.maxPaymentPerUnit ?? null,
          optionGrantPayments: a.optionGrantPayments || {},
          questions: a.questions || DEFAULT_MANUAL_QUESTIONS,
          fundingOptional: a.fundingOptional || false,
          unit: a.unit, isWholeFarm: isWholeFarmActionType(a.actionType)
      }));

      try {
          await api.put(`/events/${selectedEvent}`, {
              name: editName,
              allowedActions: finalActions,
              startDate: editStartDate,
              endDate: editEndDate,
              maxPayoutPerFarmer: editMaxPayout ? parseFloat(editMaxPayout) : null, // 👈 NEW
              minBenefitCostRatio: (editMinBenefitCostRatio === '' || editMinBenefitCostRatio === null || editMinBenefitCostRatio === undefined) ? 1 : editMinBenefitCostRatio,
              termsAndConditions: editTerms, // 👈 NEW
              eventSummary: editEventSummary,
              eventSummaryUrl: editEventSummaryUrl,
              outcomeValues: editOutcomeValues,
              fundspreaderClearingMetric: editFundspreaderClearingMetric || 'area',
              fundspreaderOutcomeToken: editFundspreaderOutcomeToken || null,
              confirmDeletion
          });
          
          alert("Saved!"); 
          setHasChanges(false); 
          loadEvents(); 
      } 
      catch (err) { 
          if (err.response?.status === 409) {
              if (window.confirm("⚠️ WARNING:\n\n" + err.response.data.msg)) {
                  handleUpdateEventSettings(true);
              }
          } else {
              alert("Update failed: " + (err.response?.data?.msg || "Server Error")); 
          }
      }
  };
  
  // --- LAYER LIBRARY HANDLERS ---
  // Each of these throws on failure — the calling components (GlobalEligibilityPanel,
  // AuctionManagement's LayerSection) own their local busy/status state and
  // surface err.response?.data?.msg themselves.
  const handleApplyGlobalEligibilityLayer = async (layerId) => {
      await api.post(`/layers/${layerId}/apply`, { eventId: null });
      await loadGlobalZones();
  };

  // Saves a layer to the tenant's library only — no eventId, so
  // materializeLayer is skipped server-side (libraryOnly flag). Admins apply
  // it to a specific auction afterwards via LayerSection's "Select a Focus
  // Area" dropdown, or to the Global Eligibility slot from that panel.
  const handleUploadToLibrary = async ({ file, name, notes, layerType }) => {
      const formData = new FormData();
      formData.append('file', file); formData.append('name', name); formData.append('notes', notes || '');
      formData.append('layerType', layerType); formData.append('libraryOnly', 'true');
      await api.post('/layers/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadLayerLibrary();
  };

  const handleApplyFocusAreaLayer = async (layerId) => {
      if (!selectedEvent) return;
      await api.post(`/layers/${layerId}/apply`, { eventId: selectedEvent });
      await Promise.all([loadZones(selectedEvent), loadEvents(), loadZoneCoverage()]);
  };

  const handleDeleteZone = async (zoneId) => {
      if(!window.confirm("Delete this zone?")) return;
      try { 
          setZones(prev => prev.filter(z => z._id !== zoneId));
          await api.delete(`/zones/${zoneId}`); 
          loadZones(selectedEvent); loadEvents(); 
      } catch(e) { alert("Failed to delete zone"); }
  };

  // Confirmation + success/failure messaging is handled by the calling
  // LayerSection (frontend/src/pages/admin/components/AuctionManagement.js) —
  // these just perform the clear and let it propagate/refresh on success.
  const handleClearZones = async () => {
      if (!selectedEvent) return;
      await api.delete(`/zones/event/${selectedEvent}`);
      await Promise.all([loadZones(selectedEvent), loadZoneCoverage()]);
  };

  const handleEditZoneWeight = async (zone) => {
      const newWeight = prompt(`Enter new multiplier for "${zone.name}":`, zone.weight);
      if (newWeight && !isNaN(newWeight)) {
          const val = parseFloat(newWeight);
          setZones(prevZones => prevZones.map(z => z._id === zone._id ? { ...z, weight: val } : z));
          try { await api.put(`/zones/${zone._id}`, { weight: val }); loadEvents(); } 
          catch (e) { alert("Update failed"); loadZones(selectedEvent); }
      }
  };

  const runSimulation = async () => {
      if (!selectedEvent) return;
      setSimLoading(true); setAuctionResults(null);
      try { 
          const res = await api.post('/optimization/run', { eventId: selectedEvent }); 
          setAuctionResults(res.data); loadEvents(); 
      } 
      catch (err) { alert("Simulation failed: " + (err.response?.data?.msg || "Error")); } 
      finally { setSimLoading(false); }
  };

  const previewManualClose = () => {
      const winners = []; const losers = []; const explored = []; let totalSpend = 0;
      liveBids.forEach(bid => {
          if (bid.status !== 'pending') return;
          const decision = bid.decisionStatus;
          const formatted = {
              farmer: bid.user?.name,
              action: bid.actionName,
              price: bid.amount,
              score: '-',
              reason: decision === 'approved' ? 'Approved' : decision === 'explore' ? 'Marked for Exploration' : decision === 'rejected' ? 'Manual Rejection' : 'No Decision Recorded (Auto-Reject)'
          };
          if (decision === 'approved') { winners.push(formatted); totalSpend += bid.amount; }
          else if (decision === 'explore') { explored.push(formatted); }
          else { losers.push(formatted); }
      });
      setAuctionResults({
          summary: { totalBids: liveBids.length, winningBids: winners.length, exploredBids: explored.length, totalSpend: { total: totalSpend } },
          winners, losers, explored
      });
  };

  const [isCommitting, setIsCommitting] = useState(false);

  const commitAuction = async () => {
      if (!selectedEvent) return;
      const msg = isManual ? "FINAL WARNING: Lock decisions, send emails, and close?" : "Commit this ALGORITHMIC event?";
      if (!window.confirm(msg)) return;
      setIsCommitting(true);
      try {
        const res = await api.post('/optimization/commit', { eventId: selectedEvent });
        alert(res.data.msg);
        setAuctionResults(null);
        setShowClosureModal(false);
        loadEvents();
        loadLiveBids(selectedEvent);
      } catch (err) {
        alert("Commit failed");
      } finally {
        setIsCommitting(false);
      }
  };
  const [isNudging, setIsNudging] = useState(false);

  const handleSendNudge = async () => {
      if (!selectedEvent) return;
      if (!window.confirm(`Are you sure you want to email all participating farmers their current odds for this event?`)) return;
      
      setIsNudging(true);
      try {
          const res = await api.post('/optimization/nudge', { eventId: selectedEvent });
          alert(`✅ Success: ${res.data.msg}`);
      } catch (err) {
          console.error(err);
          alert(err.response?.data?.msg || 'Failed to send update emails.');
      } finally {
          setIsNudging(false);
      }
  };
  const [isClosingEarly, setIsClosingEarly] = useState(false);

  const closeAuctionEarly = async (reason) => {
      if (!selectedEvent || !reason || !reason.trim()) return;
      if (!window.confirm('Close this event early? Bidders will be emailed the reason you entered. No winners/losers will be assessed.')) return;

      setIsClosingEarly(true);
      try {
          await api.post(`/events/${selectedEvent}/close-early`, { reason });
          alert('Event closed early. Bidders have been notified.');
          setShowClosureModal(false);
          setAuctionResults(null);
          loadEvents();
      } catch (err) {
          console.error(err);
          alert(err.response?.data?.msg || 'Failed to close event early.');
      } finally {
          setIsClosingEarly(false);
      }
  };

  const openClosure = (eventId) => {
      if (eventId !== selectedEvent) {
          setSelectedEvent(eventId);
          loadLiveBids(eventId);
      }
      setShowClosureModal(true);
  };

  const [archivingEventId, setArchivingEventId] = useState(null);

  const handleArchiveEvent = async (eventId) => {
      if (!window.confirm('Add this event to the Archive? It will be removed from Live Events; its report and any proposal ZIP remain available on the Event Archive page.')) return;
      setArchivingEventId(eventId);
      try {
          await api.post(`/archives/${eventId}`);
          loadEvents();
      } catch (err) {
          console.error(err);
          alert(err.response?.data?.msg || 'Failed to archive event.');
      } finally {
          setArchivingEventId(null);
      }
  };

  const currentEv = events.find(e => e._id === selectedEvent);
  const isClosed = currentEv?.status === 'closed';
  const isManual = currentEv?.auctionType === 'manual'; 

  const loadEditFormForAuction = useCallback((auctionId) => {
      const ev = events.find(e => e._id === auctionId);
      if (!ev) return;

      setSelectedEvent(auctionId);
      setEditName(ev.name || '');
      loadZones(auctionId);
      loadFocusAreaLayers();

      const currentConfig = libraryActions.map(libAction => {
          const existing = ev.allowedActions.find(a => a.actionId === libAction._id);
          return {
              _id: libAction._id,
              label: libAction.name,
              enabled: !!existing,
              budget: existing ? existing.budget : 100000,
              grantPrice: existing ? existing.grantPrice : 0,
              maxPaymentPerFarm: existing ? existing.maxPaymentPerFarm : null,
              maxContributionPercent: existing ? existing.maxContributionPercent : 100,
              maxPaymentPerUnit: existing ? existing.maxPaymentPerUnit : null,
              optionGrantPayments: existing?.optionGrantPayments ? { ...existing.optionGrantPayments } : {},
              questions: existing?.questions?.length === 3 ? [...existing.questions] : DEFAULT_MANUAL_QUESTIONS,
              fundingOptional: existing?.fundingOptional || false,
              unit: libAction.unit,
              actionType: libAction.actionType,
              variations: libAction.variations || []
          };
      });
      setEditActions(currentConfig);
      const enabledLibActions = currentConfig.filter(a => a.enabled)
          .map(ea => libraryActions.find(la => la._id === ea._id))
          .filter(Boolean);
      // Previously-saved outcome values are deliberate admin choices, so treat
      // them as "touched" — they won't get silently recomputed just because
      // Action definitions changed since this event was last saved.
      const savedOutcomeValues = ev.outcomeValues ? { ...ev.outcomeValues } : {};
      const touchedFromSaved = {};
      Object.keys(savedOutcomeValues).forEach(id => { touchedFromSaved[id] = true; });
      setEditOutcomeValues(seedOutcomeValues(computeOutcomeDefaults(enabledLibActions), savedOutcomeValues, touchedFromSaved));
      setEditOutcomeValuesTouched(touchedFromSaved);
      setEditMinBenefitCostRatio(ev.minBenefitCostRatio ?? 1);
      setEditStartDate(ev.startDate ? ev.startDate.split('T')[0] : '');
      setEditEndDate(ev.endDate ? ev.endDate.split('T')[0] : '');
      setEditMaxPayout(ev.maxPayoutPerFarmer || '');
      setEditTerms(ev.termsAndConditions || '');
      setEditEventSummary(ev.eventSummary || '');
      setEditEventSummaryUrl(ev.eventSummaryUrl || '');
      setEditFundspreaderClearingMetric(ev.fundspreaderClearingMetric || 'area');
      setEditFundspreaderOutcomeToken(ev.fundspreaderOutcomeToken || '');
      setHasChanges(false);
  }, [events, libraryActions, loadZones, loadFocusAreaLayers, computeOutcomeDefaults]);

  // Re-runs loadEditFormForAuction against the persisted event, which
  // re-derives every edit* field (including editName) from live data and
  // resets hasChanges — used by the Edit Auction modal's guarded close and
  // AuctionManagement's "Discard Changes" button.
  const handleDiscardChanges = useCallback(() => {
      if (selectedEvent) loadEditFormForAuction(selectedEvent);
  }, [selectedEvent, loadEditFormForAuction]);

  // Opens the Zones (Auction Focus Area) modal directly from the Summary of
  // Auctions table's Layers column — Zones used to be a sub-tab buried
  // inside the Edit Auction modal.
  const openZonesForAuction = useCallback((auctionId) => {
      setSelectedEvent(auctionId);
      loadZones(auctionId);
      loadFocusAreaLayers();
      setZonesModalAuctionId(auctionId);
  }, [loadZones, loadFocusAreaLayers]);

  const resetNewEventForm = useCallback(() => {
      const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
      const endDate = new Date(tomorrow); endDate.setDate(endDate.getDate() + 30);
      setNewEvent({
          name: '', startDate: tomorrow.toISOString().split('T')[0], endDate: endDate.toISOString().split('T')[0],
          auctionType: 'grants_fixed', selectedActionIds: {}, outcomeValues: {}, outcomeValuesTouched: {}, minBenefitCostRatio: 1,
          maxPayoutPerFarmer: '', termsAndConditions: '',
          eventSummary: '', eventSummaryUrl: '',
          fundspreaderClearingMetric: 'area', fundspreaderOutcomeToken: '',
          isGlobal: false, clientId: ''
      });
  }, []);

  // Copies an existing auction's settings into the Create Auction form —
  // used instead of reopening a closed auction, which is no longer allowed.
  // Name is blanked and dates are reset so the result is unambiguously a new event.
  const duplicateAuctionSettings = useCallback((auctionId) => {
      const ev = events.find(e => e._id === auctionId);
      if (!ev) return;

      const today = new Date();
      const endDate = new Date(today); endDate.setMonth(endDate.getMonth() + 1);

      const selectedActionIds = {};
      (ev.allowedActions || []).forEach(a => {
          if (!a.actionId) return;
          selectedActionIds[a.actionId] = {
              enabled: true, budget: a.budget || 0, grantPrice: a.grantPrice || 0,
              maxPaymentPerFarm: a.maxPaymentPerFarm ?? null, maxContributionPercent: a.maxContributionPercent ?? 100,
              maxPaymentPerUnit: a.maxPaymentPerUnit ?? null,
              optionGrantPayments: a.optionGrantPayments ? { ...a.optionGrantPayments } : {}
          };
      });

      setNewEvent({
          name: '',
          startDate: today.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
          auctionType: ev.auctionType || 'algorithmic',
          selectedActionIds,
          // Copied values are deliberate choices from the source auction, so
          // mark them touched — they won't get silently recomputed just
          // because the copied action selection differs from the source's.
          outcomeValues: ev.outcomeValues ? { ...ev.outcomeValues } : {},
          outcomeValuesTouched: ev.outcomeValues ? Object.fromEntries(Object.keys(ev.outcomeValues).map(id => [id, true])) : {},
          minBenefitCostRatio: ev.minBenefitCostRatio ?? 1,
          maxPayoutPerFarmer: ev.maxPayoutPerFarmer || '',
          termsAndConditions: ev.termsAndConditions || '',
          eventSummary: ev.eventSummary || '',
          eventSummaryUrl: ev.eventSummaryUrl || '',
          fundspreaderClearingMetric: ev.fundspreaderClearingMetric || 'area',
          fundspreaderOutcomeToken: ev.fundspreaderOutcomeToken || '',
          isGlobal: !!ev.isGlobal,
          clientId: ev.clientId?._id || ev.clientId || ''
      });
  }, [events]);

  useEffect(() => {
      if (activeTab === 'dashboard') {
          resetNewEventForm();
      }
  }, [activeTab, resetNewEventForm]);

  const handleOpenUserReport = (userId) => {
      setReportsModalConfig({ reportKey: 'bid_detail_export', params: { userId } });
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.colors.background }}>

      <Navbar user={user} onLogout={onLogout} pageTitle="" maxWidth="1400px" />

      {reportsModalConfig && (
        <ReportsModal
          events={events}
          initialReportKey={reportsModalConfig.reportKey}
          initialParams={reportsModalConfig.params}
          onClose={() => setReportsModalConfig(null)}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start' }}>

        <aside style={{ ...sidebarStyles.sidebar, width: isSidebarCollapsed ? '68px' : '240px' }}>
          <div style={sidebarStyles.toggleRow}>
            <button
              type="button"
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              style={sidebarStyles.toggleBtn}
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isSidebarCollapsed ? '▶' : '◀'}
            </button>
          </div>

          <nav style={sidebarStyles.nav}>
            {!isSidebarCollapsed && <div style={sidebarStyles.sectionLabel}>Manage</div>}
            {PRIMARY_NAV_ITEMS.map(item => (
              <SidebarLink
                key={item.key}
                icon={item.icon}
                label={item.label}
                active={activeTab === item.key}
                collapsed={isSidebarCollapsed}
                onClick={() => setActiveTab(item.key)}
              />
            ))}

            {COMING_SOON_ITEMS.length > 0 && (
              <>
                <div style={sidebarStyles.divider}></div>
                {!isSidebarCollapsed && <div style={sidebarStyles.sectionLabel}>Coming Soon</div>}
                {COMING_SOON_ITEMS.map(item => (
                  <SidebarLink
                    key={item.label}
                    icon={item.icon}
                    label={item.label}
                    hint={item.hint}
                    collapsed={isSidebarCollapsed}
                    disabled
                  />
                ))}
              </>
            )}

            {user?.role === 'superadmin' && (
              <>
                <div style={sidebarStyles.divider}></div>
                <SidebarLink
                  icon={<ShieldIcon />}
                  label="Super Console"
                  collapsed={isSidebarCollapsed}
                  tone="danger"
                  onClick={() => navigate('/superadmin')}
                />
              </>
            )}
          </nav>
        </aside>

        <main style={sidebarStyles.main}>
        <div style={styles.container}>

        {/* === TAB: OVERVIEW === */}
        {activeTab === 'overview' && (
            <AdminOverview
                processedEvents={processedEvents} overviewLoading={overviewLoading} overviewData={overviewData} zoneCoverage={zoneCoverage} sortConfig={sortConfig} handleSort={handleSort}
                openQuickAuctionDetails={(eventId) => setReportsModalConfig({ reportKey: 'quick_auction_details', params: { eventId } })}
                openClosure={openClosure}
                modalLiveBids={modalLiveBids} modalZones={modalZones} modalBidsLoading={modalBidsLoading} loadBidsForAuction={loadBidsForAuction}
                handleSaveDecision={handleSaveDecision}
                libraryActions={libraryActions} loadEditFormForAuction={loadEditFormForAuction}
                algorithms={algorithms}
                currentEv={currentEv} isClosed={isClosed} isManual={isManual}
                editName={editName} setEditName={setEditName}
                editStartDate={editStartDate} setEditStartDate={setEditStartDate} editEndDate={editEndDate} setEditEndDate={setEditEndDate}
                editMaxPayout={editMaxPayout} setEditMaxPayout={setEditMaxPayout} editTerms={editTerms} setEditTerms={setEditTerms}
                editMinBenefitCostRatio={editMinBenefitCostRatio} setEditMinBenefitCostRatio={setEditMinBenefitCostRatio}
                editEventSummary={editEventSummary} setEditEventSummary={setEditEventSummary} editEventSummaryUrl={editEventSummaryUrl} setEditEventSummaryUrl={setEditEventSummaryUrl}
                editActions={editActions} setEditActions={setEditActions} toggleEditAction={toggleEditAction} updateEditActionBudget={updateEditActionBudget} updateEditActionGrantPrice={updateEditActionGrantPrice}
                updateEditActionMaxPaymentPerFarm={updateEditActionMaxPaymentPerFarm} updateEditActionMaxContributionPercent={updateEditActionMaxContributionPercent}
                updateEditActionMaxPaymentPerUnit={updateEditActionMaxPaymentPerUnit}
                updateEditActionOptionGrantPayment={updateEditActionOptionGrantPayment}
                updateEditActionQuestion={updateEditActionQuestion} updateEditActionFundingOptional={updateEditActionFundingOptional}
                editOutcomeValues={editOutcomeValues} handleEditWeightChange={handleEditWeightChange}
                editFundspreaderClearingMetric={editFundspreaderClearingMetric} setEditFundspreaderClearingMetric={setEditFundspreaderClearingMetric}
                editFundspreaderOutcomeToken={editFundspreaderOutcomeToken} setEditFundspreaderOutcomeToken={setEditFundspreaderOutcomeToken}
                hasChanges={hasChanges} setHasChanges={setHasChanges} handleUpdateEventSettings={handleUpdateEventSettings} handleDiscardChanges={handleDiscardChanges}
                zones={zones} globalZones={globalZones}
                focusAreaLayers={focusAreaLayers}
                handleApplyFocusAreaLayer={handleApplyFocusAreaLayer}
                handleClearZones={handleClearZones} handleEditZoneWeight={handleEditZoneWeight} handleDeleteZone={handleDeleteZone}
                zonesModalAuctionId={zonesModalAuctionId} openZonesForAuction={openZonesForAuction} closeZonesModal={() => setZonesModalAuctionId(null)}
                handleDeleteBid={handleDeleteBid}
                handleWithdrawWinner={handleWithdrawWinner} handlePromoteBid={handlePromoteBid} fetchPromotionHeadroom={fetchPromotionHeadroom}
                fetchBackfillPreview={fetchBackfillPreview} handleBackfillWinners={handleBackfillWinners}
                handleArchiveEvent={handleArchiveEvent} archivingEventId={archivingEventId}
            />
        )}

        {/* === TAB: USER MANAGEMENT === */}
        {activeTab === 'users' && (
            <UserManagement
                handleOpenUserReport={handleOpenUserReport}
                onOpenSummaryReport={() => setReportsModalConfig({ reportKey: 'user_summary', params: {} })}
            />
        )}

        {/* === TAB: REPORTS === */}
        {activeTab === 'reports' && (
            <AdminReports events={events} />
        )}

        {/* === TAB: EVENT ARCHIVE === */}
        {activeTab === 'archive' && (
            <EventArchive
                modalLiveBids={modalLiveBids} modalZones={modalZones} modalBidsLoading={modalBidsLoading} loadBidsForAuction={loadBidsForAuction}
                handleSaveDecision={handleSaveDecision}
                handleDeleteBid={handleDeleteBid}
                handleWithdrawWinner={handleWithdrawWinner} handlePromoteBid={handlePromoteBid} fetchPromotionHeadroom={fetchPromotionHeadroom}
                fetchBackfillPreview={fetchBackfillPreview} handleBackfillWinners={handleBackfillWinners}
            />
        )}

        {/* === TAB: CONTRACTS & DOCUMENTS === */}
        {activeTab === 'contracts' && (
            <ContractsDocuments events={events} />
        )}

        {/* === TAB 4: ACTION LIBRARY === */}
        {activeTab === 'library' && (
            <ActionLibrary
                libraryActions={libraryActions}
                isActionModalOpen={isActionModalOpen} setIsActionModalOpen={setIsActionModalOpen} editingAction={editingAction} setEditingAction={setEditingAction}
                handleSaveAction={handleSaveAction} handleDeleteAction={handleDeleteAction}
                archivedActions={archivedActions} loadArchivedActions={loadArchivedActions}
                handleArchiveAction={handleArchiveAction} handleRestoreAction={handleRestoreAction}
                wholeFarmModalMode={wholeFarmModalMode} setWholeFarmModalMode={setWholeFarmModalMode} openWholeFarmModal={openWholeFarmModal}
                algorithms={algorithms}
                onOpenAlgorithmReport={() => setReportsModalConfig({ reportKey: 'action_algorithm_summary', params: {} })}
                onAlgorithmCreated={loadAlgorithms}
            />
        )}

        {/* === TAB: OUTCOME ALGORITHMS === */}
        {activeTab === 'algorithms' && (
            <AlgorithmLibrary
                algorithms={algorithms}
                isAlgorithmModalOpen={isAlgorithmModalOpen} setIsAlgorithmModalOpen={setIsAlgorithmModalOpen}
                editingAlgorithm={editingAlgorithm} setEditingAlgorithm={setEditingAlgorithm}
                handleSaveAlgorithm={handleSaveAlgorithm} handleArchiveToggleAlgorithm={handleArchiveToggleAlgorithm}
                handleUploadAlgorithmsCsv={handleUploadAlgorithmsCsv} handleDeleteAlgorithm={handleDeleteAlgorithm}
            />
        )}

        {/* === TAB: LAYER LIBRARY === */}
        {activeTab === 'layerLibrary' && (
            <LayerLibrary
                layers={layerLibrary} layerLibraryLoading={layerLibraryLoading}
                handleUploadToLibrary={handleUploadToLibrary}
                handleArchiveToggleLayer={handleArchiveToggleLayer} handleDeleteLayer={handleDeleteLayer}
                globalZones={globalZones} eligibilityLayers={eligibilityLayers}
                handleApplyGlobalEligibilityLayer={handleApplyGlobalEligibilityLayer}
            />
        )}

        {/* === TAB 5: DASHBOARD (AUCTION MGT) === */}
        {activeTab === 'dashboard' && (
            <AuctionManagement
                mode="create"
                newEvent={newEvent} setNewEvent={setNewEvent} libraryActions={libraryActions} algorithms={algorithms} user={user} clients={clients}
                toggleNewEventAction={toggleNewEventAction} updateNewEventBudget={updateNewEventBudget} updateNewEventGrantPrice={updateNewEventGrantPrice} handleCreateEvent={handleCreateEvent} handleCreateWeightChange={handleCreateWeightChange}
                updateNewEventMaxPaymentPerFarm={updateNewEventMaxPaymentPerFarm} updateNewEventMaxContributionPercent={updateNewEventMaxContributionPercent}
                updateNewEventMaxPaymentPerUnit={updateNewEventMaxPaymentPerUnit}
                updateNewEventOptionGrantPayment={updateNewEventOptionGrantPayment}
                updateNewEventQuestion={updateNewEventQuestion} updateNewEventFundingOptional={updateNewEventFundingOptional}
                handleAuctionTypeChange={handleAuctionTypeChange}
                events={processedEvents} duplicateAuctionSettings={duplicateAuctionSettings}
            />
        )}

        <ClosureModal
            showClosureModal={showClosureModal} setShowClosureModal={setShowClosureModal} currentEv={currentEv} isClosed={isClosed} isManual={isManual}
            liveBids={liveBids}
            simLoading={simLoading} runSimulation={runSimulation} auctionResults={auctionResults} setAuctionResults={setAuctionResults}
            handleSendNudge={handleSendNudge} isNudging={isNudging} commitAuction={commitAuction} isCommitting={isCommitting} previewManualClose={previewManualClose}
            closeAuctionEarly={closeAuctionEarly} isClosingEarly={isClosingEarly}
        />
        </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;